// Domain types & enums — single source of truth.
// Mirrors docs/prd/04-data-model.md (grounded in the real Airtable snapshot).
// Values below are VERBATIM from the data; keep them exact (spaces, casing, punctuation).
import { z } from "zod";

// ── Domains (5 real codes, from Domain Performance) ──────────────────────────
export const DOMAINS = ["BCS", "MedSci", "PH", "PSE", "SSH"] as const;
export type Domain = (typeof DOMAINS)[number];
export const DomainSchema = z.enum(DOMAINS);

/** Human-readable expansions — TODO(open-input): confirm BCS/PSE/SSH with the team (spec §12). */
export const DOMAIN_LABELS: Record<Domain, string> = {
  BCS: "Biological & Chemical Sciences",
  MedSci: "Medical Sciences",
  PH: "Public Health",
  PSE: "Physical Sciences & Engineering",
  SSH: "Social Sciences & Humanities",
};

/** Geographic team / country units (SoftR Team field). Fixed vocabulary for QC + Find a Preprint filters. */
export const RRID_TEAMS = ["Berkeley", "Vietnam", "India", "Mexico", "Bangladesh"] as const;
export type RridTeam = (typeof RRID_TEAMS)[number];
export const RridTeamSchema = z.enum(RRID_TEAMS);

/** Full Country / Team checklist for Find a Preprint + Quality Check filters. */
export const RRID_COUNTRY_TEAMS = [
  "Berkeley",
  "Mexico",
  "India",
  "FAMSA",
  "Peru",
  "Rwanda",
  "Vietnam",
  "South Africa",
  "Uganda",
  "Ethiopia",
  "Bangladesh",
  "Tanzania",
] as const;

/** Canonical checklist (in display order) plus any extra Team values from data (drops blank / "pilot"). */
export function countryTeamFilterOptions(live: readonly string[] = []): string[] {
  const skip = new Set(["", "pilot"]);
  const canonical = new Set<string>(RRID_COUNTRY_TEAMS);
  const extras: string[] = [];
  for (const raw of live) {
    const t = raw.trim();
    if (!t || skip.has(t.toLowerCase()) || canonical.has(t)) continue;
    extras.push(t);
  }
  extras.sort((a, b) => a.localeCompare(b));
  return [...RRID_COUNTRY_TEAMS, ...extras];
}

/** The raw `Users.Domain` / `Selected.Domain` multi-select carries non-canonical values
 *  ("Data Science", "Don't Know/Undecided"). Coerce to the 5 canonical codes; drop the rest. */
export function coerceDomain(value: unknown): Domain | null {
  if (typeof value !== "string") return null;
  return (DOMAINS as readonly string[]).includes(value) ? (value as Domain) : null;
}

// ── Selection lifecycle: Selected.Status ─────────────────────────────────────
// July-2 iteration (FR-J4): selection is DIRECT + AE-gated — `To Pitch(Editorial) → Selected`
// in one step; the earlier `Proposed for Selection` two-step is removed.
export const SELECTED_STATUSES = [
  "Unread",
  "Taken",
  "To Pitch(Team)",
  "Team Rejected", // rejected at the domain team-meeting pitch gate (Softr "Change Status")
  "To Pitch(Editorial)",
  "Editorial Rejected", // rejected at the editorial-meeting pitch gate
  "Selected",
  // Legacy SoftR discovery statuses (Team1_Preprints): a pool preprint bounced back once
  // (re-claimable) or twice (out of circulation). Imported by the Team1 sync — the webapp
  // never sets these itself (its reject gates produce Team/Editorial Rejected).
  "Rejected Once",
  "Rejected Twice",
] as const;
export type SelectedStatus = (typeof SELECTED_STATUSES)[number];
export const SelectedStatusSchema = z.enum(SELECTED_STATUSES);

/** Human-facing labels (July-8 Bucket 2) — API/Airtable values stay verbatim. */
export const SELECTED_STATUS_LABELS: Partial<Record<SelectedStatus, string>> = {
  Unread: "Unclaimed",
  Taken: "Claimed",
};

export function selectedStatusLabel(status: SelectedStatus): string {
  return SELECTED_STATUS_LABELS[status] ?? status;
}

/** Short card preview from a full abstract (discovery cards — not the full text). */
export function abstractBlurb(abstract: string | null | undefined, maxLen = 220): string | null {
  if (!abstract?.trim()) return null;
  const flat = abstract.trim().replace(/\s+/g, " ");
  if (flat.length <= maxLen) return flat;
  const cut = flat.slice(0, maxLen).replace(/\s+\S*$/, "");
  return `${cut}…`;
}

/** Selected.Proposal Status (Prophy reviewer-suggestion generation). */
export const PROPOSAL_STATUSES = [
  "Created",
  "Refined",
  "Failed (Bad PDF Link)",
  "Failed (API Error)",
  "None",
] as const;
export type ProposalStatus = (typeof PROPOSAL_STATUSES)[number];
export const ProposalStatusSchema = z.enum(PROPOSAL_STATUSES);

// ── Reviewer-pipeline lifecycle: Preprint Info ONLY.Preprint Status ──────────
export const PREPRINT_STATUSES = [
  "ACTIVE",
  "ON HOLD",
  "DORMANT",
  "INVITED",
  "COMPLETE",
  "DISCARDED",
] as const;
export type PreprintStatus = (typeof PREPRINT_STATUSES)[number];
export const PreprintStatusSchema = z.enum(PREPRINT_STATUSES);

/** Human-facing pipeline labels. API/DB values stay verbatim (`ON HOLD`, `DORMANT`).
 *  Angel 2026-08-27: revert Stefano's "Pending Reviewer Responses" back to On Hold.
 *  DORMANT stays "In Queue" until she asks to revert that too. */
export const PREPRINT_STATUS_LABELS: Partial<Record<PreprintStatus, string>> = {
  DORMANT: "In Queue",
  "ON HOLD": "On Hold",
};

export function preprintStatusLabel(status: PreprintStatus | string): string {
  return (PREPRINT_STATUS_LABELS as Record<string, string>)[status] ?? status;
}

/** Preprint Info ONLY.Reviewer Outreach Status (publish sub-stage). */
export const OUTREACH_STATUSES = [
  "Reviewer Selection in Process",
  "Zero Acceptances",
  "1 Review Accepted",
  "1 Review Published",
  "Published",
] as const;
export type OutreachStatus = (typeof OUTREACH_STATUSES)[number];

// ── Reviewer proposal status: Student Reviewer Inputs.Status ─────────────────
// July-2 iteration (FR-J13) trims the original 15 snapshot values: the H-index gates
// (Low/Extremely Low/High) are auto-captured at COI time, `Incomplete` is gone (proposals are
// complete on submit — FR-J12), the old `Invite Backup` is folded into `Invite Backup Unchecked`,
// and `Tentative Invite` is deduped into `Double Check`. `COI Detected` is KEPT: the async
// Prophy COI seam (wall 4) can still produce it until the real engine replaces the stub.
// Legacy snapshot values are coerced at the airtable-sync boundary (LEGACY_REVIEWER_STATUSES).
export const REVIEWER_STATUSES = [
  "Unchecked",
  "Invite",
  "Reject",
  "Invite Backup Unchecked",
  "COI Detected",
  "Fixable",
  "Fixed",
  "Student Reject",
  "Double Check",
] as const;
export type ReviewerStatus = (typeof REVIEWER_STATUSES)[number];
export const ReviewerStatusSchema = z.enum(REVIEWER_STATUSES);

/** SoftR-parity R-001 (Jul-16 WO): "My Reviewers" / My Quality Checks attention order —
 *  statuses needing the student's (or AE's) eyes sort first. Lower = more urgent; anything
 *  unlisted ranks last. Student Reject shares the Reject band. */
export const REVIEWER_ATTENTION_ORDER: Partial<Record<ReviewerStatus, number>> = {
  Fixable: 0,
  "COI Detected": 1,
  Reject: 2,
  "Student Reject": 2,
};

export function reviewerAttentionRank(status: ReviewerStatus): number {
  return REVIEWER_ATTENTION_ORDER[status] ?? 3;
}

/** WO-7/21 Track B: statuses that do NOT count as "truly proposed" in productivity ratios —
 *  Student Reject (withdrawn by the proposer pre-QC) and COI Detected (blocked before
 *  evaluation) never reached the AE, so they leave the greenlit-of-proposed denominator. */
export const NOT_PROPOSED_STATUSES: readonly ReviewerStatus[] = ["Student Reject", "COI Detected"];

export function countsAsProposed(status: ReviewerStatus): boolean {
  return !NOT_PROPOSED_STATUSES.includes(status);
}

/**
 * Stefano (Aug 2026): greenlit rate = Invite ÷ (proposed − Unchecked).
 * Unchecked proposals haven't been QCd yet, so they leave the decided denominator.
 */
export function greenlitRatePct(greenlit: number, proposed: number, unchecked: number): number {
  const decided = Math.max(0, proposed - unchecked);
  return decided > 0 ? Math.round((greenlit / decided) * 100) : 0;
}

/** Retired snapshot statuses → their July-2 replacement (applied at import/sync time). */
export const LEGACY_REVIEWER_STATUSES: Record<string, ReviewerStatus> = {
  "Tentative Invite": "Double Check",
  "Invite Backup": "Invite Backup Unchecked",
  Incomplete: "Unchecked",
  "Low H-index": "Unchecked",
  "Extremely Low H-index": "Unchecked",
  "High H-index": "Unchecked",
};

/** Final Status collapses the 15 to three for reporting. */
export const REVIEWER_FINAL_STATUSES = ["Unchecked", "Reject", "Invite"] as const;
export type ReviewerFinalStatus = (typeof REVIEWER_FINAL_STATUSES)[number];

// ── Candidate pool status: Referees.Status ───────────────────────────────────
// COI-12 claim lifecycle: unclaimed / claimed / incomplete / unable_to_evaluate / rejected.
// "Incomplete" is the SoftR-observed value backing the "Incomplete AI Suggested Reviewers" table.
export const REFEREE_STATUSES = [
  "Unclaimed",
  "Proposed",
  "Claimed",
  "Incomplete",
  "Reject",
  "Unable to Evaluate",
] as const;
export type RefereeStatus = (typeof REFEREE_STATUSES)[number];
export const RefereeStatusSchema = z.enum(REFEREE_STATUSES);

// ── COI (performed by Prophy) ────────────────────────────────────────────────
export const COI_STATUSES = ["Prophy Checked", "Not Checked"] as const;
export type CoiStatus = (typeof COI_STATUSES)[number];

/** COI pipeline state (COI-10 axis 1): has the engine run? Distinct from the verdict. */
export const COI_PIPELINE_STATES = ["not_checked", "checking", "checked"] as const;
export type CoiPipelineState = (typeof COI_PIPELINE_STATES)[number];

/** COI engine verdict (COI-10 axis 2). `unable_to_evaluate` = engine ran but could not decide
 *  (no Prophy author match, expired session, shadow backend). */
export const COI_VERDICTS = ["clear", "conflict", "unable_to_evaluate"] as const;
export type CoiVerdict = (typeof COI_VERDICTS)[number];

/** The four conflict reasons enumerated in COI-4 / FR-2.10 (legacy hyphenated form). */
export const COI_REASONS = [
  "already-proposed",
  "co-authorship",
  "is-author",
  "same-institution",
] as const;
export type CoiReason = (typeof COI_REASONS)[number];

/** COI-17 verdict reason codes (snake_case, stored in `coiReasons` json array). The last two are
 *  advisory flags seen in the data, not hard conflicts. */
export const COI_REASON_CODES = [
  "already_proposed",
  "co_authorship",
  "is_author",
  "same_institution",
  "affiliation_coi",
  "low_h_index",
] as const;
export type CoiReasonCode = (typeof COI_REASON_CODES)[number];

/** Bridge from the legacy hyphenated reason to its COI-17 code. */
export const COI_REASON_TO_CODE: Record<CoiReason, CoiReasonCode> = {
  "already-proposed": "already_proposed",
  "co-authorship": "co_authorship",
  "is-author": "is_author",
  "same-institution": "same_institution",
};

/** Human-readable copy for conflict reasons (COI-4: conflicts state the reason). */
export const COI_REASON_LABELS: Record<CoiReasonCode, string> = {
  already_proposed: "Already proposed",
  co_authorship: "Co-authorship with the preprint authors",
  is_author: "Reviewer is one of the preprint authors",
  same_institution: "Same institution as the preprint authors",
  affiliation_coi: "Affiliation conflict of interest",
  low_h_index: "Low h-index (advisory)",
};

// ── COI dual-write lane (cutover workorder §2.1/§2.3) ────────────────────────
// The new-infrastructure write plane: coi-api dual-writes verdict records into the webapp DB
// while Airtable/Prophy stay shadow. These vocabularies are the coi-api pipeline's, NOT the
// webapp engine's CoiVerdict above — do not merge them.

/** Verdict a coi-api check lands on (workorder §2.1, verbatim). */
export const COI_RECORD_VERDICTS = [
  "clear",
  "flag",
  "reject",
  "no_match",
  "auth_error",
  "error",
  "pending",
] as const;
export type CoiRecordVerdict = (typeof COI_RECORD_VERDICTS)[number];
export const CoiRecordVerdictSchema = z.enum(COI_RECORD_VERDICTS);

/** Who wrote the row: coi-api's dual-write path, or the Airtable forward replay. */
export const COI_INGEST_SOURCES = ["coi_api", "replay"] as const;
export type CoiIngestSource = (typeof COI_INGEST_SOURCES)[number];
export const CoiIngestSourceSchema = z.enum(COI_INGEST_SOURCES);

/** Dashboard lane a queue sample belongs to (workorder §2.1: tag samples legacy|new). */
export const COI_LANES = ["legacy", "new"] as const;
export type CoiLane = (typeof COI_LANES)[number];
export const CoiLaneSchema = z.enum(COI_LANES);

// ── App-login roles (real values from Users.Role / Users."Role- Reviewer Selection") ─
// "Reviewer" = external invitee (no app login in v1); "Hold" = deactivated — both excluded.
export const ROLES = [
  "Student",
  "Quality Checker in Training",
  "Manager",
  "Central",
  "Admin",
] as const;
export type Role = (typeof ROLES)[number];
export const RoleSchema = z.enum(ROLES);

/** Maps a raw Users.Role / Role- Reviewer Selection value to an app role, or null if excluded. */
export function coerceRole(value: unknown): Role | null {
  if (typeof value !== "string") return null;
  if ((ROLES as readonly string[]).includes(value)) return value as Role;
  // Reviewer / Hold / Dropped etc. → no app login.
  return null;
}

/** Proposer Role provenance (audit) on Student Reviewer Inputs. */
export const PROPOSER_ROLES = ["Student", "Manager", "Quality Checker in Training", "Central"] as const;
export type ProposerRole = (typeof PROPOSER_ROLES)[number];

/** Proposal Source provenance. WO mapping: student → "Student", ai → "Prophy", manual → "Manual"
 *  (values stay verbatim-from-data; "Manual" backs the "Manually add suggested reviewer" path). */
export const PROPOSAL_SOURCES = ["Student", "Prophy", "Manual"] as const;
export type ProposalSource = (typeof PROPOSAL_SOURCES)[number];

// H-index auto-gate statuses were removed in the July-2 iteration (FR-J13): H-index is
// auto-captured at COI time instead of driving dedicated reviewer statuses.
